import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, LoaderCircle } from 'lucide-react';
import { RestaurantOverviewRow } from '../../utils/allRestaurantsDashboardCalculations';
import { KpiChangeData } from '../../utils/kpiChangeCalculations';
import { eur } from '../revenuehistory/utils';
import { greenTextColor, redTextColor, primaryText, getRankBadgeStyle } from '../../styling/stylingHelper';
import { CardWithHeader } from '../ui/CardWithHeader';
import { KpiChangeBadge } from '../ui/StatCard';
import { cn } from '../../lib/utils';
import { TableComponent, ColumnFilterConfig } from '../ui/TableComponent';
import { useNavigate } from 'react-router-dom';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../shared/mobile/ResponsiveTable';

// Inline preview shows the top rows; the full ranking has its own dashboard route.
const PREVIEW_ROWS = 8;
const MOBILE_PAGE_SIZE = 20;

const MetricChange = ({ change, goodWhenUp = false, asBadge = false }: {
  change: KpiChangeData | null;
  goodWhenUp?: boolean;
  asBadge?: boolean;
}) => {
  const { t } = useTranslation();
  if (!change || !Number.isFinite(change.changeValue) || change.changeValue === 0) return null;

  const isUp = change.changeValue > 0;
  const Icon = isUp ? ArrowUp : ArrowDown;
  const label = `${t(isUp ? 'common.upTrend' : 'common.downTrend')}: ${change.changeValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}% ${change.changeLabel}`;

  if (asBadge) {
    return <KpiChangeBadge changeValue={change.changeValue} trendDirection={goodWhenUp ? 'up' : 'down'} title={label} compact />;
  }

  return (
    <span
      className={cn('inline-flex items-center gap-0.5 flex-shrink-0 text-xs font-medium whitespace-nowrap', isUp === goodWhenUp ? greenTextColor : redTextColor)}
      role="img"
      aria-label={label}
      title={label}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
    </span>
  );
};

interface RestaurantOverviewTableProps {
  data: RestaurantOverviewRow[];
  loading?: boolean;
  fullPage?: boolean;
  restaurantFilter?: {
    selectedValues: string[];
    onValuesChange: (values: string[]) => void;
    availableOptions: { id: string; name: string }[];
  };
}

export const RestaurantOverviewTable: React.FC<RestaurantOverviewTableProps> = ({ data, loading, restaurantFilter, fullPage = false }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  // Sort state managed internally for the preview and full-page table.
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(MOBILE_PAGE_SIZE);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return data;
    return [...data].sort((a, b) => {
      const aVal = sortColumn === 'costPercentChange' ? a.costPercentChange?.changeValue ?? 0 : a[sortColumn as keyof RestaurantOverviewRow] as number;
      const bVal = sortColumn === 'costPercentChange' ? b.costPercentChange?.changeValue ?? 0 : b[sortColumn as keyof RestaurantOverviewRow] as number;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortColumn, sortDirection]);

  // The rank badge reflects the currently active sort metric (revenue by default).
  // Descending / default order counts 1..N top-to-bottom. Ascending (lowest first)
  // counts upside down so the highest value still ranks #1 at the bottom.
  const rankByRestaurant = useMemo(() => {
    const map = new Map<string, number>();
    const n = sortedData.length;
    sortedData.forEach((row, index) => {
      map.set(row.restaurantId, sortDirection === 'asc' ? n - index : index + 1);
    });
    return map;
  }, [sortedData, sortDirection]);

  const columns = useMemo(() => [
    {
      key: 'restaurantName' as const,
      label: t('dashboard.restaurant'),
      render: (value: string, row: RestaurantOverviewRow) => {
        const rank = rankByRestaurant.get(row.restaurantId) ?? row.rank;
        return (
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
            style={getRankBadgeStyle(rank)}
          >
            {rank}
          </div>
          <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] font-medium truncate">
            {value}
          </span>
        </div>
        );
      },
      ...(restaurantFilter ? {
        filter: {
          selectedValues: restaurantFilter.selectedValues,
          onValuesChange: restaurantFilter.onValuesChange,
          availableOptions: restaurantFilter.availableOptions,
          filterLabel: 'Restaurants',
          align: 'left' as const,
        } as ColumnFilterConfig
      } : {}),
    },
    {
      key: 'revenue' as const,
      label: t('dashboard.revenue'),
      sortable: true,
      sortKey: 'revenue',
      render: (value: number, row: RestaurantOverviewRow) => (
        <span className="inline-flex items-center gap-1 text-[#101828] dark:text-textPrimary-dark text-[14px] font-medium">
          <span className={fullPage ? 'font-bold' : undefined}>{eur(value)}</span>
          <MetricChange change={row.revenueChange} goodWhenUp asBadge={fullPage} />
        </span>
      ),
    },
    {
      key: 'totalCost' as const,
      label: t('dashboard.totalCost'),
      sortable: true,
      sortKey: 'totalCost',
      render: (value: number, row: RestaurantOverviewRow) => (
        <span className="inline-flex items-center gap-1 text-[#101828] dark:text-textPrimary-dark text-[14px] font-medium">
          <span className={fullPage ? 'font-bold' : undefined}>{eur(value)}</span>
          <MetricChange change={row.totalCostChange} asBadge={fullPage} />
        </span>
      ),
    },
    {
      key: 'costPercent' as const,
      label: t('dashboard.costPercent'),
      sortable: true,
      sortKey: 'costPercent',
      render: (value: number, row: RestaurantOverviewRow) => {
        // Red when the restaurant is over its own food-cost target (bad), green when at/under (good).
        const overTarget = row.costTarget > 0 && value > row.costTarget;
        return (
          <span className="inline-flex items-center gap-1">
            <span
              className={cn(
                'inline-block px-2 py-1.5 rounded-[6px] text-[14px] leading-none font-semibold',
                overTarget
                  ? 'bg-[#FFF4E6] dark:bg-[#FFF4E6]/20 text-[#eb8d00] dark:text-darkOrangeColor-dark'
                  : 'bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965] dark:text-darkGreenColor-dark'
              )}
            >
              {value.toFixed(1)}%
            </span>
            {!fullPage && <MetricChange change={row.costPercentChange} />}
          </span>
        );
      },
    },
    ...(fullPage ? [{
      key: 'costPercentChange' as const,
      label: t('dashboard.costPercentChange'),
      sortable: true,
      sortKey: 'costPercentChange',
      render: (change: KpiChangeData | null) => <MetricChange change={change} asBadge />,
    }] : []),
    {
      key: 'revenueShare' as const,
      label: t('dashboard.percentOfRevenue'),
      sortable: true,
      sortKey: 'revenueShare',
      render: (value: number) => (
        <div className="flex items-center gap-2 justify-end">
          <div className="w-[40px] h-[7px] rounded-full bg-[#D1D1D6] dark:bg-gray-700 overflow-hidden flex-shrink-0">
            <div className="h-full rounded-full bg-[#0E8965] dark:bg-[#0E8965]" style={{ width: `${Math.min(value, 100)}%` }} />
          </div>
          <span className="text-[#344054] dark:text-textPrimary-dark text-[14px] font-semibold">
            {value.toFixed(1)}%
          </span>
        </div>
      ),
    },
  ], [t, restaurantFilter, rankByRestaurant, fullPage]);

  // While the viewed period's data is still streaming in, show a spinner instead of
  // a partial/empty table (mirrors the KPI cards' loading state).
  if (loading) {
    return (
      <CardWithHeader title={t('dashboard.restaurantOverview')}>
        <div className="flex items-center justify-center w-full py-16">
          <LoaderCircle className={cn('h-8 w-8 animate-spin', primaryText)} />
        </div>
      </CardWithHeader>
    );
  }

  return (
    <ResponsiveTable
      desktop={
        /* The full page uses the shared table pagination; the dashboard stays compact. */
        <TableComponent<RestaurantOverviewRow>
          title={t('dashboard.restaurantOverview')}
          data={sortedData}
          columns={columns}
          blankLines={false}
          alignOtherColumnsRight={true}
          itemsPerPage={fullPage ? undefined : PREVIEW_ROWS}
          usePagination={fullPage}
          options={{ noBlankPadding: true }}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          seeAllText={!fullPage ? t('dashboard.seeAll') : undefined}
          onSeeAllClick={!fullPage ? () => navigate('/dashboard/restaurants') : undefined}
        />
      }
      mobile={
        /* Mobile: preview cards link to the full page, which loads more incrementally. */
        <div>
          <h3 className="text-[18px] leading-6 font-semibold text-textPrimary dark:text-textPrimary-dark mb-4">
            {t('dashboard.restaurantOverview')}
          </h3>
          <MobileCardList
            items={fullPage ? sortedData.slice(0, mobileVisibleCount) : sortedData}
            keyOf={(row) => row.restaurantId}
            previewCount={3}
            viewAllHref={!fullPage ? '/dashboard/restaurants' : undefined}
            onLoadMore={fullPage ? () => setMobileVisibleCount(count => count + MOBILE_PAGE_SIZE) : undefined}
            hasMore={fullPage && mobileVisibleCount < sortedData.length}
            loadMoreLabel={t('common.loadMore')}
            viewAllLabel={t('dashboard.seeAll')}
            renderCard={(row) => {
              const rank = rankByRestaurant.get(row.restaurantId) ?? row.rank;
              const overTarget = row.costTarget > 0 && row.costPercent > row.costTarget;
              return (
                <div className="bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                        style={getRankBadgeStyle(rank)}
                      >
                        {rank}
                      </span>
                      <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] font-medium truncate">
                        {row.restaurantName}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 flex-shrink-0">
                      <span
                        className={cn(
                          'inline-block px-2 py-1.5 rounded-[6px] text-[14px] leading-none font-semibold flex-shrink-0',
                          overTarget
                            ? 'bg-[#FFF4E6] dark:bg-[#FFF4E6]/20 text-[#eb8d00] dark:text-darkOrangeColor-dark'
                            : 'bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965] dark:text-darkGreenColor-dark'
                        )}
                      >
                        {row.costPercent.toFixed(1)}%
                      </span>
                      {!fullPage && <MetricChange change={row.costPercentChange} />}
                    </span>
                  </div>
                  <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                    <MobileCardField label={t('dashboard.revenue')} value={
                      <span className={cn('inline-flex gap-1', fullPage ? 'flex-col items-start' : 'items-center')}>
                        <span className={fullPage ? 'font-bold' : undefined}>{eur(row.revenue)}</span>
                        <MetricChange change={row.revenueChange} goodWhenUp asBadge={fullPage} />
                      </span>
                    } divider />
                    <MobileCardField label={t('dashboard.totalCost')} value={
                      <span className={cn('inline-flex gap-1', fullPage ? 'flex-col items-start' : 'items-center')}>
                        <span className={fullPage ? 'font-bold' : undefined}>{eur(row.totalCost)}</span>
                        <MetricChange change={row.totalCostChange} asBadge={fullPage} />
                      </span>
                    } divider />
                    <MobileCardField label={t('dashboard.percentOfRevenue')} value={`${row.revenueShare.toFixed(1)}%`} />
                  </div>
                  {fullPage && (
                    <MobileCardField
                      className="mt-3"
                      label={t('dashboard.costPercentChange')}
                      value={<MetricChange change={row.costPercentChange} asBadge />}
                    />
                  )}
                </div>
              );
            }}
          />
        </div>
      }
    />
  );
};
